import {
  connectMcpServer,
  redactText,
  type McpServerConnection,
  type ToolDef,
  type ToolEffect,
} from "@fabric-harness/sdk";
import type { DatabricksTokenProvider } from "./identity.js";
import { databricksWorkspaceOrigin } from "./workspace-origin.js";

export type DatabricksManagedMcpEndpoint =
  | { kind: "genie-one" }
  | { kind: "genie"; spaceId: string }
  | { kind: "ai-search"; index: string }
  | { kind: "sql" }
  | { kind: "functions"; catalog: string; schema: string; functionName?: string }
  | { kind: "mcp-service"; name: string }
  | { kind: "url"; url: string | URL };

export interface DatabricksManagedMcpServerConfig {
  /** Stable connection name used in generated tool names (`mcp__<name>__<tool>`). */
  name: string;
  endpoint: DatabricksManagedMcpEndpoint;
  allowTools?: string[];
  denyTools?: string[];
  /** Explicit remote-name/glob to effect mapping. First match wins. */
  effects?: Record<string, ToolEffect>;
  /** Required for unknown tools on SQL, functions, MCP Services, and custom URLs. */
  defaultEffect?: ToolEffect;
  reconnection?: {
    initialReconnectionDelay?: number;
    maxReconnectionDelay?: number;
    reconnectionDelayGrowFactor?: number;
    maxRetries?: number;
  };
}

export interface ConnectDatabricksManagedMcpOptions {
  host: string;
  tokenProvider: DatabricksTokenProvider;
  server: DatabricksManagedMcpServerConfig;
  fetchImpl?: typeof fetch;
}

/** Build a workspace-local managed MCP or Unity AI Gateway MCP Service endpoint. */
export function databricksManagedMcpUrl(host: string, endpoint: DatabricksManagedMcpEndpoint): URL {
  const origin = databricksWorkspaceOrigin(host);
  switch (endpoint.kind) {
    case "genie-one":
      return new URL("/api/2.0/mcp/genie", origin);
    case "genie":
      return new URL(`/api/2.0/mcp/genie/${segment(endpoint.spaceId, "Genie space id")}`, origin);
    case "ai-search": {
      const [catalog, schema, index] = qualifiedName(endpoint.index, "AI Search index");
      return new URL(
        `/api/2.0/mcp/ai-search/${segment(catalog, "catalog")}/${segment(schema, "schema")}/${segment(index, "index")}`,
        origin,
      );
    }
    case "sql":
      return new URL("/api/2.0/mcp/sql", origin);
    case "functions": {
      const suffix = endpoint.functionName
        ? `/${segment(endpoint.functionName, "function name")}`
        : "";
      return new URL(
        `/api/2.0/mcp/functions/${segment(endpoint.catalog, "catalog")}/${segment(endpoint.schema, "schema")}${suffix}`,
        origin,
      );
    }
    case "mcp-service": {
      qualifiedName(endpoint.name, "MCP Service");
      return new URL(`/ai-gateway/mcp-services/${segment(endpoint.name, "MCP Service")}`, origin);
    }
    case "url": {
      const url =
        endpoint.url instanceof URL ? new URL(endpoint.url) : new URL(endpoint.url, origin);
      if (url.origin !== origin)
        throw new Error(
          `databricks managed MCP: endpoint origin ${url.origin} does not match workspace ${origin}.`,
        );
      return url;
    }
  }
}

/**
 * Connect to one Databricks managed MCP endpoint with a rotating OBO or service token.
 * Remote tools are classified before they can enter a governed bundle; unknown effects fail closed.
 */
export async function connectDatabricksManagedMcpServer(
  options: ConnectDatabricksManagedMcpOptions,
): Promise<McpServerConnection> {
  const url = databricksManagedMcpUrl(options.host, options.server.endpoint);
  const secrets = new Set<string>();
  const fetchImpl = authenticatedWorkspaceFetch(
    url.origin,
    options.tokenProvider,
    options.fetchImpl ?? fetch,
    secrets,
  );
  let inner: McpServerConnection | undefined;
  try {
    inner = await connectMcpServer(options.server.name, {
      url,
      fetch: fetchImpl,
      ...(options.server.allowTools ? { allowTools: options.server.allowTools } : {}),
      ...(options.server.denyTools ? { denyTools: options.server.denyTools } : {}),
      ...(options.server.reconnection ? { reconnection: options.server.reconnection } : {}),
    });
    const connection = inner;
    const tools: ToolDef[] = [];
    const synchronize = () => {
      const classified = connection.tools.map((tool) =>
        classifyDatabricksMcpTool(tool, url, options.server, secrets),
      );
      tools.splice(0, tools.length, ...classified);
    };
    synchronize();
    return {
      name: connection.name,
      tools,
      async reconnect() {
        await connection.reconnect();
        synchronize();
      },
      close: () => connection.close(),
    };
  } catch (error) {
    await inner?.close().catch(() => undefined);
    throw redactedMcpError(error, secrets);
  }
}

function classifyDatabricksMcpTool(
  tool: ToolDef,
  url: URL,
  config: DatabricksManagedMcpServerConfig,
  secrets: Set<string>,
): ToolDef {
  const mcp = (tool.metadata as { mcp?: { tool?: unknown } } | undefined)?.mcp;
  const remoteName = typeof mcp?.tool === "string" ? mcp.tool : tool.name;
  const effect = configuredEffect(remoteName, config);
  if (!effect) {
    throw new Error(
      `databricks managed MCP: tool "${remoteName}" from "${config.name}" has no effect classification. Add an effects glob or defaultEffect before exposing it.`,
    );
  }
  const inner = tool.execute;
  return {
    ...tool,
    metadata: {
      ...(tool.metadata ?? {}),
      effect,
      provider: "databricks",
      service: "managed-mcp",
      databricksMcp: {
        endpointKind: config.endpoint.kind,
        server: config.name,
        url: url.toString(),
      },
      governance: {
        resources: [staticGovernedResource(config, url)],
      },
    },
    ...(inner
      ? {
          async execute(input, context) {
            try {
              return await inner(input, context);
            } catch (error) {
              throw redactedMcpError(error, secrets);
            }
          },
        }
      : {}),
  };
}

function configuredEffect(
  remoteName: string,
  config: DatabricksManagedMcpServerConfig,
): ToolEffect | undefined {
  for (const [pattern, effect] of Object.entries(config.effects ?? {})) {
    if (matches(remoteName, pattern)) return effect;
  }
  if (
    config.endpoint.kind === "genie-one" ||
    config.endpoint.kind === "genie" ||
    config.endpoint.kind === "ai-search"
  )
    return "read";
  return config.defaultEffect;
}

function staticGovernedResource(config: DatabricksManagedMcpServerConfig, url: URL) {
  if (config.endpoint.kind === "mcp-service") {
    return {
      kind: "mcp-service",
      value: config.endpoint.name,
      catalogQualified: true,
    };
  }
  return { kind: "mcp-server", value: url.toString() };
}

function authenticatedWorkspaceFetch(
  workspaceOrigin: string,
  tokenProvider: DatabricksTokenProvider,
  fetchImpl: typeof fetch,
  secrets: Set<string>,
): typeof fetch {
  return async (input, init) => {
    const url = new URL(input instanceof Request ? input.url : String(input));
    if (url.origin !== workspaceOrigin) {
      throw new Error(
        `databricks managed MCP: refused to send a workspace token to ${url.origin}.`,
      );
    }
    const attempt = async (): Promise<Response> => {
      const token = await tokenProvider();
      if (!token)
        throw new Error("databricks managed MCP: token provider returned an empty token.");
      secrets.add(token);
      const headers = new Headers(input instanceof Request ? input.headers : undefined);
      new Headers(init?.headers).forEach((value, name) => headers.set(name, value));
      headers.set("authorization", `Bearer ${token}`);
      if (input instanceof Request) {
        return fetchImpl(new Request(input.clone(), { ...init, headers }));
      }
      return fetchImpl(input, { ...init, headers });
    };
    const first = await attempt();
    if (first.status !== 401) return first;
    // A 401 may mean an OBO/M2M token rotated between discovery or invocation and transport.
    // Resolve credentials again and retry exactly once; repeated rejection remains visible.
    return attempt();
  };
}

function qualifiedName(value: string, label: string): [string, string, string] {
  const parts = value.split(".");
  if (parts.length !== 3 || parts.some((part) => !part.trim())) {
    throw new Error(`databricks managed MCP: ${label} must be catalog.schema.name.`);
  }
  return parts as [string, string, string];
}

function segment(value: string, label: string): string {
  const normalized = value.trim();
  if (!normalized || normalized === "." || normalized === ".." || normalized.includes("/")) {
    throw new Error(`databricks managed MCP: ${label} must be a non-empty path segment.`);
  }
  return encodeURIComponent(normalized);
}

function matches(value: string, pattern: string): boolean {
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
  return new RegExp(`^${escaped}$`).test(value);
}

function redactedMcpError(error: unknown, secrets: Set<string>): Error {
  const message = redactText(error instanceof Error ? error.message : String(error), {
    additionalSecrets: [...secrets],
  });
  // Deliberately do not retain the original error as `cause`: remote errors can
  // echo bearer tokens and callers may serialize the complete error object.
  const redacted = new Error(message);
  redacted.name = error instanceof Error ? error.name : "Error";
  return redacted;
}
